
import React, { FunctionComponent, useEffect, useRef } from 'react';

import { parse } from 'acorn';
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/ayu-mirage.css';
import 'codemirror/mode/javascript/javascript.js';
import { UnControlled as CodeMirror } from 'react-codemirror2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCheckDouble, faTimes } from '@fortawesome/free-solid-svg-icons';


export const DEFAULT_CODE =
`
function EmptyFunction() {
  
}
`;

export interface EditorProps {
  value: string;
  onValidate: (code: string, multiple: boolean) => void;
  onCancel: () => void;
}

export const Editor: FunctionComponent<EditorProps> = ({ value, onValidate, onCancel }) => {

  const codeMirror = useRef<any | undefined>();

  const currentValue = useRef<string>();
  useEffect(() => {
    currentValue.current = value;
  }, [ value ]);

  const handleChange = (editor: any, data: any, value: string) => {
    currentValue.current = value;
  };

  const handleValidate = (multiple = false) => {
    codeMirror.current.clearGutter('error');
    codeMirror.current.getAllMarks().map((mark: any) => mark.clear());
    try {
      parse(currentValue.current ?? '', { ecmaVersion: 'latest' });
    } catch (error) {
      console.warn(error.message);
      if (!!codeMirror.current) {
        codeMirror.current.markText(
          { line: error.loc.line - 1, ch: error.loc.column },
          { line: error.loc.line - 1, ch: error.loc.column + 100 },
          {className: 'syntax-error'}
        );
        const marker = document.createElement('div');
        marker.className = 'w-3 h-2 rounded-full mt-2';
        marker.style.backgroundColor = 'red';
        codeMirror.current.setGutterMarker(error.loc.line - 1, 'error', marker);
      }
    }
    onValidate(currentValue.current ?? '', multiple);
  }

  const handleCancel = () => {
    onCancel();
  };

  return (
    <div className="absolute top-0 left-0 w-full h-full z-20 bg-gray-800 bg-opacity-50 flex justify-center items-center">
      <div className="w-3/5 h-3/5 bg-gray-200 rounded-md relative">
        <div className="bg-yellow-400 rounded-t-md flex flex-row justify-center text-gray-900">
          <span className="font-bold">Code Editor</span>
        </div>
        <CodeMirror
          className="h-full"
          value={value}
          editorDidMount={editor => {codeMirror.current = editor; (window as any).editor = editor}}
          onChange={(editor, data, value) => handleChange(editor, data, value)}
          options={{ theme: 'ayu-mirage', mode: 'javascript', lineNumbers: true, gutters: ['error', 'CodeMirror-linenumbers'] }}
        />
        <div className="absolute right-2 bottom-2 z-10">
          <button
            title="Cancel"
            onClick={handleCancel}
            className="rounded-full bg-gray-200 w-6 my-2 mr-2 text-gray-800 hover:bg-gray-400 hover:text-red-600"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
          <button
            title="Save only for the current function"
            onClick={() => handleValidate(false)}
            className="rounded-full bg-gray-200 w-6 my-2 mr-2 text-gray-800 hover:bg-gray-400 hover:text-green-200"
          >
            <FontAwesomeIcon icon={faCheck} />
          </button>
          <button
            onClick={() => handleValidate(true)}
            title="Save for all cloned functions"
            className="rounded-full bg-gray-200 w-6 my-2 mr-2 text-gray-800 hover:bg-gray-400 hover:text-green-200"
          >
            <FontAwesomeIcon icon={faCheckDouble} />
          </button>
        </div>
      </div>
    </div>
  );
};

